/**
 * Parsing and formatting for the human-friendly date/time format used in
 * `src/data/site-config.json` and `src/data/notifications.json`:
 *
 *   DD Mon YYYY, h:mm AM/PM        e.g. "31 Aug 2026, 2:00 PM"
 *
 * Every value in that format is interpreted as India Standard Time
 * (Asia/Kolkata, UTC+05:30). India does not observe daylight saving, so the
 * offset is a fixed constant rather than something that needs a timezone
 * database — the one thing that makes it safe to hardcode here instead of
 * pulling in a date library.
 */

const MONTHS = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec']
const IST_OFFSET_MS = 5.5 * 60 * 60 * 1000

const PATTERN = /^(\d{1,2})\s+([A-Za-z]{3,})\s+(\d{4}),\s*(\d{1,2}):(\d{2})\s*(AM|PM)$/i

/**
 * Returns the UTC epoch millisecond the given IST wall-clock string refers to,
 * or `null` if the string doesn't match the required shape or names a date
 * that doesn't exist (e.g. "31 Feb 2026"). Callers are expected to fail open
 * on `null` rather than throw — a typo in a hand-edited JSON file must never
 * take the site down.
 */
export const parseIstDateTime = (value: string | null | undefined): number | null => {
  if (!value) return null
  const match = PATTERN.exec(value.trim())
  if (!match) return null

  const [, dayStr, monthStr, yearStr, hourStr, minuteStr, meridiem] = match
  const month = MONTHS.indexOf(monthStr.slice(0, 3).toLowerCase())
  const day = Number(dayStr)
  const year = Number(yearStr)
  const minute = Number(minuteStr)
  let hour = Number(hourStr)

  if (month === -1 || day < 1 || day > 31 || hour < 1 || hour > 12 || minute > 59) return null

  hour = hour % 12
  if (meridiem.toUpperCase() === 'PM') hour += 12

  // `Date.UTC` silently normalises an out-of-range day (e.g. Feb 31 becomes
  // Mar 3) instead of failing, so the only way to catch it is to render the
  // result back out and compare it to what was asked for.
  const asUtc = new Date(Date.UTC(year, month, day, hour, minute))
  if (asUtc.getUTCFullYear() !== year || asUtc.getUTCMonth() !== month || asUtc.getUTCDate() !== day) {
    return null
  }

  return asUtc.getTime() - IST_OFFSET_MS
}

const istParts = (ms: number) => {
  const fmt = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Asia/Kolkata',
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  })
  const parts = fmt.formatToParts(new Date(ms))
  const get = (type: string) => parts.find((p) => p.type === type)?.value ?? ''
  return {
    day: get('day'),
    month: get('month'),
    year: get('year'),
    hour: get('hour'),
    minute: get('minute'),
    meridiem: get('dayPeriod').toUpperCase(),
  }
}

/** "31 Aug 2026" — used where only the day matters (the announcements archive). */
export const formatIstDate = (ms: number): string => {
  const p = istParts(ms)
  return `${p.day} ${p.month} ${p.year}`
}

/** "31 Aug 2026, 4:00 PM IST" — used wherever a specific moment matters. */
export const formatIstDateTime = (ms: number): string => {
  const p = istParts(ms)
  return `${p.day} ${p.month} ${p.year}, ${p.hour}:${p.minute} ${p.meridiem} IST`
}
